import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../context/AnalyticsContext';

const { 
  FiTool, FiCalendar, FiClock, FiShield, FiCheckCircle, 
  FiStar, FiCreditCard, FiArrowRight, FiAlertTriangle
} = FiIcons;

const Services = () => {
  const { trackEvent } = useAnalytics();
  const [activeService, setActiveService] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    service: '',
    date: '',
    time: '',
    notes: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    trackEvent('form_fill', { type: 'service_booking' });
    setFormSubmitted(true);
    
    // In a real implementation, you'd send the form data to a server here
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        phone: '',
        vehicle: '',
        service: '',
        date: '',
        time: '',
        notes: ''
      });
      setFormSubmitted(false);
      setShowBookingForm(false);
    }, 3000);
  };

  const services = [
    {
      id: 'maintenance',
      icon: FiTool,
      title: 'Scheduled Maintenance',
      description: 'Keep your Audi performing at its best with factory-recommended maintenance. Our certified technicians use genuine Audi parts to ensure your vehicle maintains its exceptional performance, reliability, and value.',
      details: [
        'Oil and filter changes',
        'Brake system service',
        'Multi-point inspections',
        'Fluid checks and replacements',
        'Filter replacements',
        'Tire rotation and balancing'
      ]
    },
    {
      id: 'repairs',
      icon: FiAlertTriangle,
      title: 'Diagnostic & Repairs',
      description: 'Our advanced diagnostic equipment and Audi-certified technicians can quickly identify and resolve any issues with your vehicle. From electronic systems to mechanical repairs, we have the expertise to keep your Audi running perfectly.',
      details: [
        'Engine diagnostics and repair',
        'Electrical system troubleshooting',
        'Transmission service and repair',
        'Suspension and steering repairs',
        'Climate control system service',
        'Advanced driver assistance systems calibration'
      ]
    },
    {
      id: 'bodywork',
      icon: FiStar,
      title: 'Body Shop & Detailing',
      description: 'Restore your Audi to its original beauty with our expert body shop services. From minor scratches to major collision repair, our technicians use factory-approved techniques and materials to ensure perfect results.',
      details: [
        'Collision repair',
        'Paint matching and refinishing',
        'Dent removal',
        'Windshield replacement',
        'Full detailing services',
        'Paint protection treatments'
      ]
    },
    {
      id: 'tires',
      icon: FiCreditCard,
      title: 'Tire Center',
      description: 'Find the perfect tires for your Audi and Nova Scotia driving conditions. We offer a complete range of premium tires, expert installation, and seasonal tire storage to make ownership convenient.',
      details: [
        'Premium tire brands',
        'Winter tire packages',
        'Tire rotation and balancing',
        'Wheel alignment',
        'Seasonal tire storage',
        'Tire pressure monitoring system service'
      ]
    },
    {
      id: 'warranty',
      icon: FiShield,
      title: 'Warranty Services',
      description: 'Our service center is fully authorized to perform all warranty repairs. We ensure that your warranty coverage is maintained with proper documentation and genuine Audi parts.',
      details: [
        'New vehicle warranty service',
        'Certified Pre-Owned warranty repairs',
        'Extended warranty coverage',
        'Recall and service campaigns',
        'Warranty documentation',
        'Audi Care maintenance packages'
      ]
    },
    {
      id: 'accessories',
      icon: FiCheckCircle,
      title: 'Parts & Accessories',
      description: 'Enhance your Audi with genuine parts and accessories designed specifically for your model. From performance upgrades to style enhancements, we offer a complete range of Audi-approved products.',
      details: [
        'Genuine Audi parts',
        'Performance upgrades',
        'Styling accessories',
        'Roof racks and carriers',
        'Technology and connectivity upgrades',
        'Floor mats and interior accessories'
      ]
    },
  ];

  const servicePerks = [
    {
      icon: FiClock,
      title: 'Express Service',
      description: 'Quick service for routine maintenance with no appointment necessary.'
    },
    {
      icon: FiShield,
      title: 'Certified Technicians',
      description: 'Factory-trained specialists with Audi certification and ongoing education.'
    },
    {
      icon: FiCheckCircle,
      title: 'Genuine Parts',
      description: 'Only authentic Audi parts that meet strict quality and performance standards.'
    },
    {
      icon: FiCalendar,
      title: 'Online Booking',
      description: 'Convenient scheduling through our online service portal anytime.'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-black text-white">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1487754180451-c456f719a1fc?w=1920&h=600&fit=crop&crop=center" 
            alt="Audi service center" 
            className="w-full h-full object-cover opacity-60" 
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-light mb-6 uppercase tracking-tight">
              Audi Service Centre
            </h1>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto font-light">
              Expert care for your Audi from factory-trained technicians using the latest technology and genuine parts.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setShowBookingForm(true);
                  trackEvent('button_click', { type: 'book_service' });
                }}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Book Service Appointment
              </button>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-black transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Contact Service Department
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Expert Audi Service
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              At Halifax Audi, we're committed to providing exceptional service for your vehicle. 
              Our state-of-the-art facility and factory-trained technicians ensure your Audi 
              receives the premium care it deserves.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {servicePerks.map((perk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 shadow-md hover:shadow-lg transition-shadow duration-200 border border-gray-100"
              >
                <div className="bg-red-600 p-3 rounded-full w-fit mb-4">
                  <SafeIcon icon={perk.icon} className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  {perk.title}
                </h3>
                <p className="text-gray-600">
                  {perk.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a comprehensive range of services to keep your Audi in perfect condition.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                onClick={() => setActiveService(activeService === service.id ? null : service.id)}
              >
                <div className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="bg-red-600 p-3 rounded-none">
                      <SafeIcon icon={service.icon} className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                        {service.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{service.description}</p>
                      
                      {activeService === service.id && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-4 border-t border-gray-200"
                        >
                          <h4 className="text-lg font-medium text-gray-900 mb-2">Services Include:</h4>
                          <ul className="space-y-2">
                            {service.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start space-x-2">
                                <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600 mt-0.5" />
                                <span className="text-gray-700">{detail}</span>
                              </li>
                            ))}
                          </ul>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowBookingForm(true);
                              setFormData(prev => ({ ...prev, service: service.title }));
                              trackEvent('button_click', { type: 'book_specific_service', service: service.title });
                            }}
                            className="mt-6 bg-red-600 text-white px-6 py-2 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm inline-flex items-center space-x-2"
                          >
                            <span>Book This Service</span>
                            <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                          </button>
                        </motion.div>
                      )}
                      
                      <button 
                        className="mt-2 text-red-600 hover:text-red-700 transition-colors inline-flex items-center space-x-1 text-sm uppercase tracking-wider"
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveService(activeService === service.id ? null : service.id);
                        }}
                      >
                        <span>{activeService === service.id ? 'Show Less' : 'Learn More'}</span>
                        <SafeIcon 
                          icon={FiArrowRight} 
                          className={`w-4 h-4 transform transition-transform ${activeService === service.id ? 'rotate-90' : ''}`} 
                        />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Audi Care Maintenance Plans */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Audi Care Maintenance Plans
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Prepaid maintenance plans that cover scheduled services for your Audi. 
                Lock in today's prices for future maintenance and enjoy the peace of mind 
                that comes with knowing your vehicle will receive genuine Audi parts and 
                expert service.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Predictable Costs</h3>
                    <p className="text-gray-600">Lock in maintenance costs at today's prices</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Convenience</h3>
                    <p className="text-gray-600">Pre-paid service with no additional out-of-pocket expenses</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Increased Resale Value</h3>
                    <p className="text-gray-600">Full Audi service history enhances your vehicle's value</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600 mt-1" />
                  <div>
                    <h3 className="font-medium text-lg">Transferable</h3>
                    <p className="text-gray-600">Plans can be transferred to the new owner if you sell your Audi</p>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    setShowBookingForm(true);
                    trackEvent('button_click', { type: 'audi_care_inquiry' });
                  }}
                  className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
                >
                  Inquire About Audi Care
                </button>
                <Link
                  to="/contact"
                  className="border border-black text-black px-8 py-3 font-medium hover:bg-gray-100 transition-colors duration-200 uppercase tracking-wider text-sm"
                >
                  Learn More
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <img 
                src="https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=800" 
                alt="Audi service technician" 
                className="rounded-lg shadow-xl" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Facility */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Our Service Facility
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our state-of-the-art service center is equipped with the latest diagnostic 
              equipment and staffed by factory-trained technicians to provide your Audi 
              with the care it deserves.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white shadow-md overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?w=800&h=500&fit=crop&crop=center" 
                alt="Service bay" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  Advanced Equipment
                </h3>
                <p className="text-gray-600">
                  Our service bays feature the latest diagnostic tools and equipment specifically 
                  designed for Audi vehicles, ensuring accurate diagnosis and efficient repairs.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white shadow-md overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1630046127397-1fb4c3a7d114?w=800&h=500&fit=crop&crop=center" 
                alt="Technician working" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  Certified Technicians
                </h3>
                <p className="text-gray-600">
                  Our technicians undergo rigorous Audi training and certification to ensure they 
                  have the expertise to service your vehicle according to factory standards.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white shadow-md overflow-hidden"
            >
              <img 
                src="https://images.unsplash.com/photo-1517524008697-84bbe3c3fd98?w=800&h=500&fit=crop&crop=center" 
                alt="Customer lounge" 
                className="w-full h-48 object-cover"
              />
              <div className="p-6">
                <h3 className="text-xl font-light text-gray-900 mb-2 uppercase tracking-tight">
                  Premium Customer Lounge
                </h3>
                <p className="text-gray-600">
                  Wait in comfort in our customer lounge featuring complimentary WiFi, 
                  refreshments, and workspaces so you can remain productive during your visit.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Hours and Info */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Service Hours
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-red-600" />
                    <span className="font-medium">Monday - Friday</span>
                  </div>
                  <span>7:30 AM - 5:30 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-red-600" />
                    <span className="font-medium">Saturday</span>
                  </div>
                  <span>8:00 AM - 4:00 PM</span>
                </div>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-red-600" />
                    <span className="font-medium">Sunday</span>
                  </div>
                  <span>Closed</span>
                </div>
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-300">
                <h4 className="text-xl font-light text-gray-900 mb-4 uppercase tracking-tight">
                  Express Service
                </h4>
                <p className="text-gray-600 mb-4">
                  No appointment necessary for oil changes and minor maintenance.
                </p>
                <div className="flex justify-between items-center">
                  <div className="flex items-center space-x-2">
                    <SafeIcon icon={FiClock} className="w-5 h-5 text-red-600" />
                    <span className="font-medium">Monday - Friday</span>
                  </div>
                  <span>7:30 AM - 3:00 PM</span>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-gray-50 p-8"
            >
              <h3 className="text-2xl font-light text-gray-900 mb-6 uppercase tracking-tight">
                Contact Service Department
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Phone</h4>
                  <a 
                    href="tel:+19024557890" 
                    className="text-red-600 hover:text-red-700 transition-colors font-medium text-lg"
                  >
                    (902) 455-7890 ext. 2
                  </a>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Email</h4>
                  <a 
                    href="mailto:service@halifaxaudi.ca" 
                    className="text-red-600 hover:text-red-700 transition-colors"
                  >
                    service@halifaxaudi.ca
                  </a>
                </div>
                
                <div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Address</h4>
                  <p className="text-gray-600">
                    3687 Kempt Road<br />
                    Halifax, NS B3K 4X5
                  </p>
                </div>
                
                <div className="pt-6">
                  <button
                    onClick={() => {
                      setShowBookingForm(true);
                      trackEvent('button_click', { type: 'book_service_from_hours' });
                    }}
                    className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm w-full"
                  >
                    Book Service Appointment
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Service Specials */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-4 uppercase tracking-tight">
              Current Service Specials
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Take advantage of these limited-time offers on our most popular services.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 overflow-hidden"
            >
              <div className="bg-red-600 text-white p-3">
                <h3 className="text-lg font-medium uppercase text-center">Summer Service Special</h3>
              </div>
              <div className="p-6">
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-gray-900">$129.95</span>
                  <span className="text-gray-600 line-through ml-2">$169.95</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Synthetic oil change</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Tire rotation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Multi-point inspection</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Brake inspection</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Fluid top-off</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mb-6">
                  Valid until September 30, 2024. Cannot be combined with other offers.
                </p>
                <button
                  onClick={() => {
                    setShowBookingForm(true);
                    setFormData(prev => ({ ...prev, service: 'Summer Service Special' }));
                    trackEvent('button_click', { type: 'book_service_special', special: 'Summer Service Special' });
                  }}
                  className="w-full bg-black text-white py-2 px-4 font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm"
                >
                  Book Now
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 overflow-hidden"
            >
              <div className="bg-red-600 text-white p-3">
                <h3 className="text-lg font-medium uppercase text-center">Brake Special</h3>
              </div>
              <div className="p-6">
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-gray-900">15% OFF</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Front or rear brake service</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Genuine Audi brake pads</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Brake fluid flush</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Caliper inspection</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Rotor resurfacing if needed</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mb-6">
                  Valid until October 31, 2024. Discount applies to parts and labor.
                </p>
                <button
                  onClick={() => {
                    setShowBookingForm(true);
                    setFormData(prev => ({ ...prev, service: 'Brake Special' }));
                    trackEvent('button_click', { type: 'book_service_special', special: 'Brake Special' });
                  }}
                  className="w-full bg-black text-white py-2 px-4 font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm"
                >
                  Book Now
                </button>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 overflow-hidden"
            >
              <div className="bg-red-600 text-white p-3">
                <h3 className="text-lg font-medium uppercase text-center">Winter Prep Package</h3>
              </div>
              <div className="p-6">
                <div className="text-center mb-4">
                  <span className="text-3xl font-bold text-gray-900">$249.95</span>
                  <span className="text-gray-600 line-through ml-2">$299.95</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Winter tire installation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Battery test</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Cooling system check</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Winter wiper blade installation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <SafeIcon icon={FiCheckCircle} className="w-5 h-5 text-red-600" />
                    <span>Undercarriage rust inspection</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mb-6">
                  Book by November 15, 2024. Tires not included. Storage available.
                </p>
                <button
                  onClick={() => {
                    setShowBookingForm(true);
                    setFormData(prev => ({ ...prev, service: 'Winter Prep Package' }));
                    trackEvent('button_click', { type: 'book_service_special', special: 'Winter Prep Package' });
                  }}
                  className="w-full bg-black text-white py-2 px-4 font-medium hover:bg-gray-900 transition-colors text-center uppercase tracking-wider text-sm"
                >
                  Book Now
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-light mb-4 uppercase tracking-tight">
              Experience Expert Audi Service
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto font-light">
              Keep your Audi performing at its best with factory-trained technicians and genuine parts. 
              Schedule your service appointment today.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setShowBookingForm(true);
                  trackEvent('button_click', { type: 'book_service_from_cta' });
                }}
                className="bg-red-600 text-white px-8 py-3 font-medium hover:bg-red-700 transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Book Service Appointment
              </button>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 font-medium hover:bg-white hover:text-black transition-colors duration-200 uppercase tracking-wider text-sm"
              >
                Contact Service Department
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Service Booking Form Modal */}
      {showBookingForm && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
          >
            {formSubmitted ? (
              <div className="text-center py-6">
                <div className="bg-green-100 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Service Appointment Requested
                </h3>
                <p className="text-gray-600 mb-4">
                  Thank you for scheduling your service appointment. A member of our service team will contact you shortly to confirm your appointment details.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-gray-900 mb-6">
                  Schedule Service Appointment
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name*
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address*
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number*
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Vehicle Information*
                    </label>
                    <input
                      type="text"
                      name="vehicle"
                      value={formData.vehicle}
                      onChange={handleInputChange}
                      placeholder="Year, Model (e.g., 2022 Audi Q5)"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Service Type*
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    >
                      <option value="">Select Service</option>
                      <option value="Scheduled Maintenance">Scheduled Maintenance</option>
                      <option value="Oil Change">Oil Change</option>
                      <option value="Tire Service">Tire Service</option>
                      <option value="Brake Service">Brake Service</option>
                      <option value="Diagnostic">Diagnostic</option>
                      <option value="Repair">Repair</option>
                      <option value="Summer Service Special">Summer Service Special</option>
                      <option value="Brake Special">Brake Special</option>
                      <option value="Winter Prep Package">Winter Prep Package</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Date*
                      </label>
                      <input
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Time*
                      </label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleInputChange}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                      >
                        <option value="">Select Time</option>
                        <option value="8:00 AM">8:00 AM</option>
                        <option value="9:00 AM">9:00 AM</option>
                        <option value="10:00 AM">10:00 AM</option>
                        <option value="11:00 AM">11:00 AM</option>
                        <option value="12:00 PM">12:00 PM</option>
                        <option value="1:00 PM">1:00 PM</option>
                        <option value="2:00 PM">2:00 PM</option>
                        <option value="3:00 PM">3:00 PM</option>
                        <option value="4:00 PM">4:00 PM</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Notes
                    </label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Please provide any additional details about your service needs"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
                    ></textarea>
                  </div>
                  <div className="flex space-x-3 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg font-semibold hover:bg-red-700 transition-colors"
                    >
                      Schedule Appointment
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowBookingForm(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default Services;